import { useState, useCallback, useEffect } from "react";

/**
 * Generic localStorage hook with safe get/parse/set and SecurityError handling.
 *
 * Pattern consistent with useOnboardingTour: try/catch guards for SecurityError
 * (private mode, blocked storage) and JSON parse errors.
 *
 * @param key - localStorage key
 * @param fallback - default value when key is missing or unreadable
 * @returns [value, setValue] tuple
 */
export function useLocalStorage<T>(
  key: string,
  fallback: T
): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw === null) return fallback;
      return JSON.parse(raw) as T;
    } catch {
      // SecurityError or JSON parse error → graceful fallback
      return fallback;
    }
  });

  // Re-sync state when key changes (e.g. user switch)
  // fallback intentionally excluded from deps — only react to key changes
  useEffect(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw === null) {
        setValue(fallback);
        return;
      }
      setValue(JSON.parse(raw) as T);
    } catch {
      setValue(fallback);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const setAndPersist = useCallback(
    (newValue: T) => {
      setValue(newValue);
      try {
        localStorage.setItem(key, JSON.stringify(newValue));
      } catch {
        // SecurityError / QuotaExceededError → graceful fallback, state already in memory
      }
    },
    [key]
  );

  return [value, setAndPersist];
}
